import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import * as JSZip from 'jszip';

// Components
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import FileExplorer from './components/FileExplorer';
import ValidationPanel from './components/ValidationPanel';
import VersionControl from './components/VersionControl';
import Visualization from './components/Visualization';
import Packaging from './components/Packaging';
import LoadingScreen from './components/LoadingScreen';
import MarkdownEditor from './components/editors/MarkdownEditor';
import TSVEditor from './components/editors/TSVEditor';
import ImageEditor from './components/editors/ImageEditor';
import DriveBrowserModal from './components/DriveBrowserModal';

// Services
import FileSystemAccessService from './services/FileSystemAccessService';
import IndexingService from './services/IndexingService';
import ValidationService from './services/ValidationService';
import PackageApiService from './services/PackageApiService';
import GoogleDriveService, { DEFAULT_CHUNK_POLICY } from './services/GoogleDriveService';
import ZipPackagingService from './services/ZipPackagingService';

// Types
import { AppState, FileItem, PackageConfig } from './types';

type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'ae-data-manager-theme';

const getInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme === 'light' || savedTheme === 'dark') {
    return savedTheme;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const shouldFollowSystemTheme = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  return savedTheme !== 'light' && savedTheme !== 'dark';
};

const BINARY_EXTENSION_ALLOWLIST = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.avif', '.ico',
  '.glb', '.gltf', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.zip', '.rar', '.7z', '.tar', '.gz', '.bz2', '.xz', '.bin', '.wasm'
]);

const BINARY_MIME_PREFIXES = ['image/', 'audio/', 'video/', 'model/'];

const BINARY_EXACT_MIME_TYPES = new Set([
  'application/octet-stream',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-7z-compressed',
  'application/vnd.rar',
  'application/x-tar',
  'application/gzip',
  'application/wasm'
]);

const EXTENSION_MIME_FALLBACKS: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.bmp': 'image/bmp',
  '.webp': 'image/webp',
  '.avif': 'image/avif',
  '.ico': 'image/x-icon',
  '.glb': 'model/gltf-binary',
  '.gltf': 'model/gltf+json',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.zip': 'application/zip',
  '.rar': 'application/vnd.rar',
  '.7z': 'application/x-7z-compressed',
  '.tar': 'application/x-tar',
  '.gz': 'application/gzip',
  '.bz2': 'application/x-bzip2',
  '.xz': 'application/x-xz',
  '.bin': 'application/octet-stream',
  '.wasm': 'application/wasm'
};

const normalizeMimeType = (mimeType?: string): string => (mimeType || '').toLowerCase().trim();

const getFileMimeType = (file: FileItem): string | undefined => {
  return (file as FileItem & { mimeType?: string }).mimeType;
};

const resolveMimeTypeForFile = (file: FileItem): string | undefined => {
  const normalizedMimeType = normalizeMimeType(getFileMimeType(file));
  if (normalizedMimeType) {
    return normalizedMimeType;
  }

  const extension = (file.extension || '').toLowerCase();
  return EXTENSION_MIME_FALLBACKS[extension];
};

const isBinaryFile = (file: FileItem): boolean => {
  const extension = (file.extension || '').toLowerCase();
  const normalizedMimeType = normalizeMimeType(getFileMimeType(file));

  if (normalizedMimeType) {
    if (BINARY_MIME_PREFIXES.some(prefix => normalizedMimeType.startsWith(prefix))) {
      return true;
    }

    if (BINARY_EXACT_MIME_TYPES.has(normalizedMimeType)) {
      return true;
    }
  }

  // MIME can be empty for local files; extension fallback keeps binary docs lossless.
  return BINARY_EXTENSION_ALLOWLIST.has(extension);
};

const DRIVE_WARN_FILE_BYTES = 512 * 1024 * 1024;
const DRIVE_HARD_FILE_BYTES = 2 * 1024 * 1024 * 1024;
const DRIVE_HARD_PACKAGE_BYTES = 8 * 1024 * 1024 * 1024;
const STREAMING_ZIP_MIN_TOTAL_BYTES = 512 * 1024 * 1024;

const DRIVE_CHUNK_POLICY = {
  ...DEFAULT_CHUNK_POLICY,
  maxRetriesPerChunk: 5,
  initialBackoffMs: 1000,
  maxBackoffMs: 30000
};

const shouldUseStreamingZip = (totalPackageBytes = 0): boolean => {
  return totalPackageBytes >= STREAMING_ZIP_MIN_TOTAL_BYTES;
};

type DriveReadMode = 'direct' | 'chunked' | 'blocked';

const getDriveReadMode = (sizeInBytes = 0): DriveReadMode => {
  if (sizeInBytes <= 0 || sizeInBytes <= DRIVE_CHUNK_POLICY.directReadMaxBytes) {
    return 'direct';
  }

  if (sizeInBytes <= DRIVE_HARD_FILE_BYTES) {
    return 'chunked';
  }

  return 'blocked';
};

const isObjectUrl = (value: unknown): value is string => {
  return typeof value === 'string' && value.startsWith('blob:');
};

const App: React.FC = () => {
  const [appState, setAppState] = useState<AppState>({
    currentFiles: [],
    selectedFile: null,
    validationState: {
      isValidating: false,
      progress: 0,
      results: []
    },
    versions: [],
    packages: [],
    packageDirectory: {
      isAvailable: false,
      packageCount: 0
    },
    remotePackages: [],
    transferState: {
      isTransferring: false,
      operation: null,
      progress: 0,
      error: null
    },
    remotePackageState: {
      isConfigured: false,
      isConnected: false,
      error: null
    },
    visualizationData: null,
    isLoading: false,
    error: null,
    projectSource: 'local',
    driveProject: {
      isConfigured: false,
      isConnected: false,
      grantedScopes: [],
      pickerStatus: 'idle',
      pickerFailureReason: null,
      warnings: [],
      error: null
    }
  });

  const [activeView, setActiveView] = useState<'explorer' | 'validation' | 'version' | 'visualization' | 'packaging'>('explorer');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [editingFile, setEditingFile] = useState<FileItem | null>(null);
  const [editorType, setEditorType] = useState<'markdown' | 'tsv' | 'image' | null>(null);
  const [isRefreshingFiles, setIsRefreshingFiles] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme());
  const [followSystemTheme, setFollowSystemTheme] = useState<boolean>(() => shouldFollowSystemTheme());
  const [showDriveBrowser, setShowDriveBrowser] = useState(false);
  const hasHydratedRemoteStateRef = useRef(false);
  const selectedPreviewObjectUrlRef = useRef<string | null>(null);

  // Initialize services
  const [fileSystemService] = useState(() => new FileSystemAccessService());
  const [indexingService] = useState(() => new IndexingService());
  const [packageApiService] = useState(() => new PackageApiService());
  const [googleDriveService] = useState(() => new GoogleDriveService());
  const [zipPackagingService] = useState(() => new ZipPackagingService());

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const envStatus = packageApiService.getEnvironmentConfig();
    const connectionStatus = packageApiService.getConnectionStatus();

    console.info('[App] Initial remote package status snapshot', {
      envStatus,
      connectionStatus,
      appRemoteState: appState.remotePackageState
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    console.info('[App] remotePackageState changed', {
      appRemoteState: appState.remotePackageState,
      serviceConnectionStatus: packageApiService.getConnectionStatus()
    });
  }, [appState.remotePackageState, packageApiService]);

  useEffect(() => {
    // Initialize the application
    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    document.body.setAttribute('data-theme', theme);
    root.setAttribute('data-theme', theme);
    root.style.colorScheme = theme;
  }, [theme]);

  useEffect(() => {
    if (!followSystemTheme) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setTheme(event.matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [followSystemTheme]);

  useEffect(() => {
    const nextUrl = isObjectUrl(appState.selectedFile?.content) ? appState.selectedFile.content : null;
    const previousUrl = selectedPreviewObjectUrlRef.current;

    if (previousUrl && previousUrl !== nextUrl) {
      URL.revokeObjectURL(previousUrl);
    }

    selectedPreviewObjectUrlRef.current = nextUrl;
  }, [appState.selectedFile?.content]);

  useEffect(() => {
    return () => {
      const activeUrl = selectedPreviewObjectUrlRef.current;
      if (activeUrl) {
        URL.revokeObjectURL(activeUrl);
      }
    };
  }, []);

  const handleThemeToggle = () => {
    setTheme(prev => {
      const nextTheme: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      return nextTheme;
    });
    setFollowSystemTheme(false);
  };

  const hydrateRemoteStateFromService = async () => {
    if (hasHydratedRemoteStateRef.current) {
      return;
    }

    hasHydratedRemoteStateRef.current = true;

    const envStatus = packageApiService.getEnvironmentConfig();
    const serviceStatus = packageApiService.getConnectionStatus();
    const envError = envStatus.errors?.length ? envStatus.errors.join('; ') : null;

    setAppState(prev => ({
      ...prev,
      remotePackageState: {
        ...prev.remotePackageState,
        isConfigured: serviceStatus.isConfigured,
        endpoint: serviceStatus.endpoint || (envStatus.hasEndpoint ? packageApiService.endpoint : prev.remotePackageState.endpoint),
        bearerToken: envStatus.hasToken ? packageApiService.bearerToken : prev.remotePackageState.bearerToken,
        isConnected: serviceStatus.isConnected,
        lastSync: serviceStatus.lastSync || prev.remotePackageState.lastSync,
        error: serviceStatus.error || envError
      }
    }));

    if (process.env.NODE_ENV === 'development') {
      console.info('[App] hydrateRemoteStateFromService() snapshot', {
        envStatus,
        serviceStatus
      });
    }

    if (serviceStatus.isConfigured) {
      await handleRefreshRemotePackages();
    }
  };

  const initializeApp = async () => {
    setAppState(prev => ({ ...prev, isLoading: true }));
    
    try {
      let startupWarning: string | null = null;

      // Check if File System Access API is supported
      if (!fileSystemService.isSupported) {
        startupWarning = 'File System Access API is not supported in this browser. Some features will be limited.';
      }

      // Hydrate remote state from environment-backed PackageApiService and auto-refresh inventory when configured
      await hydrateRemoteStateFromService();
      
      const isDriveReady = googleDriveService.isReady();
      console.log('[App] Initialize: Drive status', { isDriveReady, isConfigured: appState.driveProject.isConfigured });

      setAppState(prev => ({
        ...prev,
        driveProject: {
          ...prev.driveProject,
          isConfigured: isDriveReady,
        },
        error: startupWarning,
        isLoading: false
      }));
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to initialize application',
        isLoading: false
      }));
    }
  };

  const isDriveFile = (file: FileItem): boolean => {
    return file.source === 'google-drive' || Boolean(file.driveMeta?.id);
  };

  const readProjectFile = async (
    file: FileItem,
    options: {
      forPackaging?: boolean;
      onProgress?: (progress: number) => void;
      onRetry?: (event: { attempt: number; backoffMs: number; fileName: string }) => void;
    } = {}
  ) => {
    if (isDriveFile(file)) {
      const fileSize = Number(file.size || 0);
      const readMode = getDriveReadMode(fileSize);

      if (readMode === 'blocked') {
        throw new Error(`${file.name} exceeds the supported in-browser Drive read limit.`);
      }

      const requireRawBlob = options.forPackaging && isBinaryFile(file);

      return await googleDriveService.readFile(file, {
        chunkPolicy: DRIVE_CHUNK_POLICY,
        requireRawBlob,
        onProgress: options.onProgress,
        onRetry: options.onRetry
      });
    }

    return await fileSystemService.readFile(file);
  };

  const saveProjectFile = async (file: FileItem, content: any) => {
    if (isDriveFile(file)) {
      const granted = await googleDriveService.ensureWriteAccess();
      if (!granted) {
        throw new Error('Google Drive write permission is required to save changes.');
      }

      await googleDriveService.saveFile(file, content);
      return;
    }

    await fileSystemService.saveFile(file, content);
  };

  const openProjectFolder = async () => {
    // Check if File System API is supported before attempting to open folder
    if (!fileSystemService.isSupported) {
      setAppState(prev => ({
        ...prev,
        error: 'Cannot open folder: File System Access API is not supported in this browser. Please use Chrome, Edge, or Opera for full functionality.'
      }));
      return;
    }
    
    setAppState(prev => ({ ...prev, isLoading: true }));
    
    try {
      // Open project folder using File System Access API
      const directoryHandle = await fileSystemService.openProjectFolder();
      
      // Index all files in the project
      const files = await fileSystemService.indexProjectFiles();
      
      // Create project index for caching
      const projectId = directoryHandle.name;
      await indexingService.createProjectIndex(projectId, files);
      
      setAppState(prev => ({
        ...prev,
        currentFiles: files,
        selectedFile: null,
        projectSource: 'local',
        driveProject: {
          ...prev.driveProject,
          isConnected: false,
          pickerStatus: 'idle',
          pickerFailureReason: null,
          warnings: [],
          error: null
        },
        isLoading: false,
        error: null
      }));
      
      console.log('Project folder opened successfully');
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to open project folder',
        isLoading: false
      }));
    }
  };

  const openDriveProjectFolder = async () => {
    if (!googleDriveService.isReady()) {
      setAppState(prev => ({
        ...prev,
        error: 'Google Drive integration is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in your environment.',
        driveProject: {
          ...prev.driveProject,
          isConfigured: false,
          isConnected: false,
          pickerStatus: 'error',
          error: 'Google Drive is not configured.'
        }
      }));
      return;
    }

    setAppState(prev => ({ ...prev, isLoading: true }));

    try {
      const session = await googleDriveService.signInReadOnly();

      let pickerResult;
      try {
        pickerResult = await googleDriveService.openPickerWithRetry({
          fallbackFolder: appState.driveProject.folderId
            ? {
                id: appState.driveProject.folderId,
                name: appState.driveProject.folderName || appState.driveProject.folderId
              }
            : undefined,
          allowFallbackOnCancel: false
        });
      } catch (pickerError) {
        // Fallback to custom browser if picker fails
        const errorCode = (pickerError as Error & { code?: string }).code;
        // Don't fallback on user cancel, unless they want to?
        // But if the page refreshes, we won't get here.
        // If picker fails with other errors, show custom browser.
        if (errorCode !== 'GOOGLE_PICKER_CANCELLED') {
           console.warn('Google Picker failed, falling back to custom browser', pickerError);
           setAppState(prev => ({ ...prev, isLoading: false }));
           setShowDriveBrowser(true);
           return;
        }
        throw pickerError;
      }

      await loadDriveProject(pickerResult.folder, session.grantedScopes, pickerResult.usedFallback ? 'fallback' : 'picked', pickerResult.fallbackReason);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to open Google Drive folder';
      const errorCode = (error as Error & { code?: string }).code || 'GOOGLE_DRIVE_ERROR';

      if (errorCode === 'GOOGLE_PICKER_CANCELLED' && appState.driveProject.folderId) {
        const usePrevious = window.confirm('Folder selection was cancelled. Continue with previously selected Drive folder?');

        if (usePrevious) {
          try {
            const previousFolder = {
              id: appState.driveProject.folderId,
              name: appState.driveProject.folderName || appState.driveProject.folderId
            };
            await loadDriveProject(previousFolder, [], 'fallback', 'GOOGLE_PICKER_CANCELLED');
            return;
          } catch (fallbackError) {
            const fallbackMessage = fallbackError instanceof Error ? fallbackError.message : 'Failed to load previously selected Drive folder';
            setAppState(prev => ({
              ...prev,
              isLoading: false,
              driveProject: {
                ...prev.driveProject,
                pickerStatus: 'error',
                pickerFailureReason: 'GOOGLE_PICKER_CANCELLED',
                error: fallbackMessage
              },
              error: fallbackMessage
            }));
            return;
          }
        }
      }

      setAppState(prev => ({
        ...prev,
        isLoading: false,
        driveProject: {
          ...prev.driveProject,
          isConfigured: googleDriveService.isReady(),
          isConnected: false,
          pickerStatus: errorCode === 'GOOGLE_PICKER_CANCELLED' ? 'cancelled' : 'error',
          pickerFailureReason: errorCode,
          error: errorMessage
        },
        transferState: {
          ...prev.transferState,
          pickerFailureReason: errorCode
        },
        error: errorMessage
      }));
    }
  };

  const loadDriveProject = async (folder: { id: string, name: string }, grantedScopes: string[] = [], pickerStatus: any = 'picked', pickerFailureReason: any = null) => {
      const { files, warnings } = await googleDriveService.listFolderFilesRecursive(folder.id);
      await indexingService.createProjectIndex(`google-drive:${folder.id}`, files);

      setAppState(prev => ({
        ...prev,
        currentFiles: files,
        selectedFile: null,
        projectSource: 'google-drive',
        driveProject: {
          ...prev.driveProject,
          isConfigured: true,
          isConnected: true,
          grantedScopes: grantedScopes.length > 0 ? grantedScopes : prev.driveProject.grantedScopes,
          folderId: folder.id,
          folderName: folder.name,
          pickerStatus,
          pickerFailureReason,
          warnings: warnings.map(item => item.message),
          error: null
        },
        isLoading: false,
        error: warnings.length > 0 ? warnings[0].message : null
      }));
  };

  const handleDriveBrowserSelect = async (folder: { id: string, name: string }) => {
     setAppState(prev => ({ ...prev, isLoading: true }));
     try {
         await loadDriveProject(folder);
     } catch (error) {
         setAppState(prev => ({
             ...prev,
             isLoading: false,
             error: error instanceof Error ? error.message : 'Failed to load selected Drive folder'
         }));
     }
  };

  const refreshProjectFiles = async () => {
    if (appState.projectSource === 'google-drive') {
      if (!appState.driveProject.folderId) {
        setAppState(prev => ({
          ...prev,
          error: 'No Drive folder selected. Open a Drive folder first.'
        }));
        return;
      }

      setIsRefreshingFiles(true);

      try {
        const { files, warnings } = await googleDriveService.listFolderFilesRecursive(appState.driveProject.folderId);

        setAppState(prev => {
          const selectedPath = prev.selectedFile?.path;
          const refreshedSelected = selectedPath
            ? files.find(file => file.path === selectedPath) || null
            : null;

          return {
            ...prev,
            currentFiles: files,
            selectedFile: refreshedSelected
              ? {
                  ...refreshedSelected,
                  content: prev.selectedFile?.path === refreshedSelected.path
                    ? prev.selectedFile.content
                    : undefined
                }
              : null,
            driveProject: {
              ...prev.driveProject,
              warnings: warnings.map(item => item.message),
              error: null
            },
            error: warnings.length > 0 ? warnings[0].message : null
          };
        });
      } catch (error) {
        setAppState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to refresh Drive file listing'
        }));
      } finally {
        setIsRefreshingFiles(false);
      }

      return;
    }

    if (!fileSystemService.directoryHandle) {
      setAppState(prev => ({
        ...prev,
        error: 'No project folder selected. Open a project folder first.'
      }));
      return;
    }

    setIsRefreshingFiles(true);

    try {
      const files = await fileSystemService.indexProjectFiles();

      setAppState(prev => {
        const selectedPath = prev.selectedFile?.path;
        const refreshedSelected = selectedPath
          ? files.find(file => file.path === selectedPath) || null
          : null;

        return {
          ...prev,
          currentFiles: files,
          selectedFile: refreshedSelected
            ? {
                ...refreshedSelected,
                content: prev.selectedFile?.path === refreshedSelected.path
                  ? prev.selectedFile.content
                  : undefined
              }
            : null,
          error: null
        };
      });
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to refresh file listing'
      }));
    } finally {
      setIsRefreshingFiles(false);
    }
  };

  const handleFileSelect = async (file: FileItem | null) => {
    if (!file) {
      setAppState(prev => ({ ...prev, selectedFile: null }));
      return;
    }

    setAppState(prev => ({ ...prev, selectedFile: file }));
    
    // Load file content
    try {
      const content = await readProjectFile(file);
      const updatedFile = { ...file, content };
      setAppState(prev => ({ ...prev, selectedFile: updatedFile }));
    } catch (error) {
      console.error(`Failed to load file ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleFileEdit = async (file: FileItem) => {
    // Determine editor type based on file extension
    const extension = file.extension?.toLowerCase() || '';
    
    // Check for TSV-content .txt files BEFORE the generic .txt → markdown fallback
    if (extension === '.tsv' || (extension === '.txt' && (
      file.name.includes('events') ||
      file.name.includes('clusters') ||
      file.name.includes('referencepoints')
    ))) {
      setEditorType('tsv');
    } else if (extension === '.md' || extension === '.txt') {
      setEditorType('markdown');
    } else if (['.png', '.jpg', '.jpeg'].includes(extension)) {
      setEditorType('image');
    } else {
      console.error('No editor available for this file type');
      return;
    }
    
    // Ensure file content is loaded before opening editor
    let fileToEdit = file;
    if (!file.content) {
      try {
        const content = await readProjectFile(file);
        fileToEdit = { ...file, content };
      } catch (error) {
        console.error(`Failed to load file ${file.name} for editing: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return;
      }
    }
    
    setEditingFile(fileToEdit);
  };

  const handleFileSave = async (file: FileItem, content: any) => {
    try {
      await saveProjectFile(file, content);

      // Update file in state
      setAppState(prev => ({
        ...prev,
        currentFiles: prev.currentFiles.map(f =>
          f.path === file.path ? { ...f, content } : f
        ),
        selectedFile: prev.selectedFile?.path === file.path
          ? { ...prev.selectedFile, content }
          : prev.selectedFile,
        driveProject: isDriveFile(file)
          ? {
              ...prev.driveProject,
              grantedScopes: Array.from(new Set([...prev.driveProject.grantedScopes, googleDriveService.getWriteScope()]))
            }
          : prev.driveProject
      }));
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  const handleFileDownload = async (file: FileItem) => {
    try {
      let content = file.content;

      if (content === undefined) {
        content = await readProjectFile(file, { forPackaging: isBinaryFile(file) });
      }

      const extension = file.extension?.toLowerCase() || '';
      const fileMimeType = resolveMimeTypeForFile(file);
      const binaryFile = isBinaryFile(file);
      let blob: Blob;

      if (binaryFile && typeof content === 'string' && (content.startsWith('blob:') || content.startsWith('data:'))) {
        const response = await fetch(content);
        blob = await response.blob();
      } else if (content instanceof Blob) {
        blob = content;
      } else if (content instanceof ArrayBuffer) {
        blob = new Blob([content], { type: fileMimeType || 'application/octet-stream' });
      } else if (ArrayBuffer.isView(content)) {
        blob = new Blob([content], { type: fileMimeType || 'application/octet-stream' });
      } else if (extension === '.json') {
        blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
      } else if (Array.isArray(content)) {
        const tsv = fileSystemService.convertToTSV(content);
        blob = new Blob([tsv], { type: 'text/tab-separated-values;charset=utf-8' });
      } else if (typeof content === 'string') {
        blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      } else {
        blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
      }

      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : `Failed to download ${file.name}`
      }));
      return false;
    }
  };

  const handleFileDelete = async (file: FileItem) => {
    try {
      if (isDriveFile(file)) {
        throw new Error('Deleting files directly from Google Drive is not implemented yet.');
      }

      await fileSystemService.deleteFile(file);

      setAppState(prev => ({
        ...prev,
        currentFiles: prev.currentFiles.filter(f => f.path !== file.path),
        selectedFile: prev.selectedFile?.path === file.path ? null : prev.selectedFile,
        error: null
      }));

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : `Failed to delete ${file.name}`
      }));
      return false;
    }
  };

  const handleEditorClose = () => {
    setEditingFile(null);
    setEditorType(null);
  };

  const handleValidation = async () => {
    setAppState(prev => ({
      ...prev,
      validationState: {
        ...prev.validationState,
        isValidating: true,
        progress: 0,
        results: []
      }
    }));

    try {
      const results = await ValidationService.validateFiles(
        appState.currentFiles,
        (progress: number) => {
          setAppState(prev => ({
            ...prev,
            validationState: {
              ...prev.validationState,
              progress
            }
          }));
        },
        async (file: FileItem) => {
          return await readProjectFile(file);
        }
      );

      setAppState(prev => ({
        ...prev,
        validationState: {
          isValidating: false,
          progress: 100,
          results
        }
      }));
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        validationState: {
          ...prev.validationState,
          isValidating: false,
          progress: 0
        },
        error: error instanceof Error ? error.message : 'Validation failed'
      }));
    }
  };

  const serializePackageFileContent = async (file: FileItem, content: any) => {
    const binaryFile = isBinaryFile(file);

    // Preserve exact JSON for history_metadata.json to prevent TSV conversion
    if (file.name === 'history_metadata.json' && typeof content === 'object') {
      return JSON.stringify(content, null, 2);
    }

    if (content instanceof Blob) {
      return await content.arrayBuffer();
    }

    if (content instanceof ArrayBuffer) {
      return content;
    }

    if (ArrayBuffer.isView(content)) {
      return content.buffer.slice(content.byteOffset, content.byteOffset + content.byteLength);
    }

    if (typeof content === 'string') {
      if (
        binaryFile &&
        (content.startsWith('blob:') || content.startsWith('data:'))
      ) {
        const response = await fetch(content);
        return await response.arrayBuffer();
      }

      if (binaryFile) {
        return new TextEncoder().encode(content).buffer;
      }

      return content;
    }

    if (Array.isArray(content)) {
      return fileSystemService.convertToTSV(content);
    }

    if (content !== null && typeof content === 'object') {
      if (binaryFile) {
        return new TextEncoder().encode(JSON.stringify(content)).buffer;
      }

      return JSON.stringify(content, null, 2);
    }

    if (binaryFile) {
      return new TextEncoder().encode(String(content ?? '')).buffer;
    }

    return String(content ?? '');
  };

  const handleOpenPackageDirectory = async () => {
    try {
      const directoryHandle = await fileSystemService.openPackageDirectory();
      const localPackages = await fileSystemService.scanPackageDirectory();

      setAppState(prev => ({
        ...prev,
        packages: localPackages,
        packageDirectory: {
          directoryHandle,
          directoryPath: directoryHandle.name,
          isAvailable: true,
          lastScanned: new Date(),
          packageCount: localPackages.length
        },
        error: null
      }));

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to open package directory'
      }));
      return false;
    }
  };

  const handleRefreshLocalPackages = async () => {
    if (!fileSystemService.packageDirectoryHandle) {
      setAppState(prev => ({
        ...prev,
        error: 'No package directory selected. Select package directory first.'
      }));
      return false;
    }

    try {
      const localPackages = await fileSystemService.scanPackageDirectory();

      setAppState(prev => ({
        ...prev,
        packages: localPackages,
        packageDirectory: {
          ...prev.packageDirectory,
          directoryHandle: fileSystemService.packageDirectoryHandle,
          directoryPath: fileSystemService.getPackageDirectoryName() || prev.packageDirectory.directoryPath,
          isAvailable: true,
          lastScanned: new Date(),
          packageCount: localPackages.length
        },
        error: null
      }));

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to refresh local packages'
      }));
      return false;
    }
  };

  const handleCreatePackage = async (packageConfig: PackageConfig) => {
    if (!fileSystemService.packageDirectoryHandle) {
      setAppState(prev => ({
        ...prev,
        error: 'No package directory selected. Select package directory first.'
      }));
      return false;
    }

    const selectedFilesSet = new Set(packageConfig.selectedFiles || []);

    const filesToPackage = appState.currentFiles.filter(file => {
      if (selectedFilesSet.size > 0 && !selectedFilesSet.has(file.path)) {
        return false;
      }

      const extension = (file.extension || '').toLowerCase();
      if (['.png', '.jpg', '.jpeg'].includes(extension) && !packageConfig.includeTextures) {
        return false;
      }

      if (['.glb', '.gltf'].includes(extension) && !packageConfig.includeModels) {
        return false;
      }

      if (file.name.toLowerCase().includes('history') && !packageConfig.includeHistory) {
        return false;
      }

      return true;
    });

    const oversizedFile = filesToPackage.find(file => Number(file.size || 0) > DRIVE_HARD_FILE_BYTES);
    if (oversizedFile) {
      setAppState(prev => ({
        ...prev,
        error: `${oversizedFile.name} exceeds the hard limit of 2 GB and cannot be packaged in-browser.`
      }));
      return false;
    }

    const totalPackageBytes = filesToPackage.reduce((sum, file) => sum + Number(file.size || 0), 0);
    if (totalPackageBytes > DRIVE_HARD_PACKAGE_BYTES) {
      setAppState(prev => ({
        ...prev,
        error: 'Selected files exceed the 8 GB package safety limit for browser packaging.'
      }));
      return false;
    }

    if (filesToPackage.some(file => Number(file.size || 0) > DRIVE_WARN_FILE_BYTES)) {
      setAppState(prev => ({
        ...prev,
        error: 'Large files detected (>512 MB). Packaging will continue with chunked downloads and may take longer.'
      }));
    }

    const packageBaseName = `${packageConfig.name}-${packageConfig.version}`;
    const nowIso = new Date().toISOString();
    const manifest = {
      schemaVersion: '1.1.0',
      name: packageConfig.name,
      version: packageConfig.version,
      description: packageConfig.description,
      createdAt: nowIso,
      fileCount: filesToPackage.length,
      files: filesToPackage.map(file => {
        const manifestFile = {
          name: file.name,
          path: file.path,
          size: file.size || 0,
          extension: file.extension || ''
        } as {
          name: string;
          path: string;
          size: number;
          extension: string;
          mimeType?: string;
        };

        const mimeType = resolveMimeTypeForFile(file);
        if (mimeType) {
          manifestFile.mimeType = mimeType;
        }

        return manifestFile;
      }),
      config: packageConfig
    };

    setAppState(prev => ({
      ...prev,
      transferState: {
        ...prev.transferState,
        isTransferring: true,
        operation: 'download',
        progress: 0,
        currentFile: filesToPackage[0]?.name,
        retryCount: 0,
        error: null
      }
    }));

    const useStreamingZip = shouldUseStreamingZip(totalPackageBytes);
    const canStreamZip = zipPackagingService.supportsStreamingZip(fileSystemService);

    if (useStreamingZip && !canStreamZip) {
      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          error: 'Streaming ZIP writer is unavailable in this browser/session. Use a Chromium browser and re-select a package directory.'
        },
        error: 'Streaming ZIP writer is unavailable in this browser/session. Use a Chromium browser and re-select a package directory.'
      }));
      return false;
    }

    try {
      if (useStreamingZip && canStreamZip) {
        const { writable } = await fileSystemService.createPackageWritableStream(packageBaseName);
        const totalEntries = filesToPackage.length + 1;
        let completedEntries = 0;

        const streamEntries = async function* () {
          for (const file of filesToPackage) {
            const fileIndex = completedEntries;
            const fileContent = file.content !== undefined
              ? file.content
              : await readProjectFile(file, {
                  forPackaging: true,
                  onProgress: (progress) => {
                    const normalizedProgress = Math.max(0, Math.min(100, progress));
                    const overallProgress = Math.round(
                      ((fileIndex + (normalizedProgress / 100)) / Math.max(totalEntries, 1)) * 100
                    );

                    setAppState(prev => ({
                      ...prev,
                      transferState: {
                        ...prev.transferState,
                        isTransferring: true,
                        operation: 'download',
                        currentFile: file.name,
                        progress: overallProgress,
                        error: null
                      }
                    }));
                  },
                  onRetry: (event) => {
                    setAppState(prev => ({
                      ...prev,
                      transferState: {
                        ...prev.transferState,
                        isTransferring: true,
                        operation: 'download',
                        currentFile: event.fileName,
                        retryCount: event.attempt,
                        error: `Retrying chunk for ${event.fileName} (attempt ${event.attempt}, waiting ${Math.round(event.backoffMs / 1000)}s)`
                      }
                    }));
                  }
                });

            const serializedContent = await serializePackageFileContent(file, fileContent);
            yield {
              path: file.path,
              content: serializedContent,
              store: isBinaryFile(file)
            };
          }

          yield {
            path: 'package-manifest.json',
            content: JSON.stringify(manifest, null, 2),
            store: false
          };
        };

        await zipPackagingService.writeStreamingZip({
          writable,
          entries: streamEntries(),
          compressionLevel: packageConfig.compressionLevel,
          onEntryComplete: ({ path }) => {
            completedEntries += 1;
            const displayName = path === 'package-manifest.json'
              ? path
              : (path.split('/').pop() || path);

            setAppState(prev => ({
              ...prev,
              transferState: {
                ...prev.transferState,
                isTransferring: true,
                operation: 'download',
                currentFile: displayName,
                progress: Math.round((completedEntries / Math.max(totalEntries, 1)) * 100),
                retryCount: 0,
                error: null
              }
            }));
          }
        });
      } else {
        const zip = new JSZip();
        const totalFiles = filesToPackage.length;

        // TS2802 Fix: Use traditional for loop instead of entries() iterator
        for (let index = 0; index < totalFiles; index++) {
          const file = filesToPackage[index];
          const fileContent = file.content !== undefined
            ? file.content
            : await readProjectFile(file, {
                forPackaging: true,
                onProgress: (progress) => {
                  setAppState(prev => ({
                    ...prev,
                    transferState: {
                      ...prev.transferState,
                      isTransferring: true,
                      operation: 'download',
                      currentFile: file.name,
                      progress
                    }
                  }));
                },
                onRetry: (event) => {
                  setAppState(prev => ({
                    ...prev,
                    transferState: {
                      ...prev.transferState,
                      isTransferring: true,
                      operation: 'download',
                      currentFile: event.fileName,
                      retryCount: event.attempt,
                      error: `Retrying chunk for ${event.fileName} (attempt ${event.attempt}, waiting ${Math.round(event.backoffMs / 1000)}s)`
                    }
                  }));
                }
              });

          const serializedContent = await serializePackageFileContent(file, fileContent);
          zip.file(file.path, serializedContent as any);

          setAppState(prev => ({
            ...prev,
            transferState: {
              ...prev.transferState,
              isTransferring: true,
              operation: 'download',
              currentFile: file.name,
              progress: Math.round(((index + 1) / Math.max(totalFiles, 1)) * 100),
              error: null
            }
          }));
        }

        zip.file('package-manifest.json', JSON.stringify(manifest, null, 2));

        const zipBlob = await zip.generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: {
            level: packageConfig.compressionLevel
          }
        });

        await fileSystemService.writePackageToDirectory(packageBaseName, zipBlob);
      }

      await handleRefreshLocalPackages();

      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          progress: 100,
          retryCount: 0,
          error: null
        }
      }));

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          error: error instanceof Error ? error.message : 'Failed while reading files for package creation'
        },
        error: error instanceof Error ? error.message : 'Failed to create package'
      }));
      return false;
    }
  };

  const handleDownloadLocalPackage = async (packageName: string) => {
    try {
      const finalName = packageName.endsWith('.zip') ? packageName : `${packageName}.zip`;
      const blob = await fileSystemService.readPackageBlob(finalName);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : `Failed to download ${packageName}`
      }));
      return false;
    }
  };

  const handleDeleteLocalPackage = async (packageName: string) => {
    try {
      await fileSystemService.deletePackageFromDirectory(packageName);
      await handleRefreshLocalPackages();
      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : `Failed to delete ${packageName}`
      }));
      return false;
    }
  };

  const handleConfigureRemote = async (endpoint: string, bearerToken: string) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.info('[App] handleConfigureRemote() called', {
          endpointPreview: endpoint ? `${endpoint.slice(0, 60)}...` : '',
          tokenLength: bearerToken ? bearerToken.length : 0,
          envStatus: packageApiService.getEnvironmentConfig(),
          beforeStatus: packageApiService.getConnectionStatus()
        });
      }

      packageApiService.configure(endpoint, bearerToken);
      const connected = await packageApiService.testConnection();
      const status = packageApiService.getConnectionStatus();

      if (process.env.NODE_ENV === 'development') {
        console.info('[App] handleConfigureRemote() result', {
          connected,
          afterStatus: status
        });
      }

      setAppState(prev => ({
        ...prev,
        remotePackageState: {
          isConfigured: status.isConfigured,
          endpoint: status.endpoint,
          bearerToken,
          isConnected: connected,
          lastSync: status.lastSync || prev.remotePackageState.lastSync,
          error: status.error || null
        },
        error: connected ? null : (status.error || 'Remote connection test failed')
      }));

      if (connected) {
        await handleRefreshRemotePackages();
      }

      return connected;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        remotePackageState: {
          ...prev.remotePackageState,
          isConfigured: false,
          isConnected: false,
          error: error instanceof Error ? error.message : 'Failed to configure remote API'
        },
        error: error instanceof Error ? error.message : 'Failed to configure remote API'
      }));
      return false;
    }
  };

  const handleRefreshRemotePackages = async () => {
    const status = packageApiService.getConnectionStatus();

    if (process.env.NODE_ENV === 'development') {
      console.info('[App] handleRefreshRemotePackages() invoked', {
        serviceStatus: status,
        appRemoteState: appState.remotePackageState
      });
    }

    if (!status.isConfigured) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[App] handleRefreshRemotePackages() aborted: service not configured', {
          serviceStatus: status,
          envStatus: packageApiService.getEnvironmentConfig()
        });
      }

      setAppState(prev => ({
        ...prev,
        error: 'Remote API is not configured. Configure endpoint and bearer token first.'
      }));
      return false;
    }

    try {
      const remotePackages = await packageApiService.listPackages();

      if (process.env.NODE_ENV === 'development') {
        console.info('[App] handleRefreshRemotePackages() success', {
          remotePackageCount: remotePackages.length
        });
      }

      setAppState(prev => ({
        ...prev,
        remotePackages,
        remotePackageState: {
          ...prev.remotePackageState,
          isConfigured: true,
          endpoint: status.endpoint,
          isConnected: true,
          lastSync: new Date(),
          error: null
        },
        error: null
      }));

      return true;
    } catch (error) {
      const connection = packageApiService.getConnectionStatus();
      setAppState(prev => ({
        ...prev,
        remotePackageState: {
          ...prev.remotePackageState,
          isConnected: false,
          error: connection.error || (error instanceof Error ? error.message : 'Failed to refresh remote packages')
        },
        error: error instanceof Error ? error.message : 'Failed to refresh remote packages'
      }));
      return false;
    }
  };

  const handleUploadPackage = async (packageName: string) => {
    const status = packageApiService.getConnectionStatus();
    if (!status.isConfigured) {
      setAppState(prev => ({
        ...prev,
        error: 'Remote API is not configured. Configure endpoint and bearer token first.'
      }));
      return false;
    }

    try {
      const finalName = packageName.endsWith('.zip') ? packageName : `${packageName}.zip`;

      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: true,
          operation: 'upload',
          progress: 0,
          currentFile: finalName,
          error: null
        }
      }));

      const packageBlob = await fileSystemService.readPackageBlob(finalName);
      await packageApiService.uploadPackageBlob(finalName, packageBlob, (progress: number) => {
        setAppState(prev => ({
          ...prev,
          transferState: {
            ...prev.transferState,
            isTransferring: true,
            operation: 'upload',
            progress,
            currentFile: finalName,
            error: null
          }
        }));
      });

      await handleRefreshRemotePackages();

      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          progress: 100,
          currentFile: finalName,
          error: null
        }
      }));

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          error: error instanceof Error ? error.message : `Failed to upload ${packageName}`
        },
        error: error instanceof Error ? error.message : `Failed to upload ${packageName}`
      }));
      return false;
    }
  };

  const handleDownloadRemotePackage = async (packageName: string) => {
    const status = packageApiService.getConnectionStatus();
    if (!status.isConfigured) {
      setAppState(prev => ({
        ...prev,
        error: 'Remote API is not configured. Configure endpoint and bearer token first.'
      }));
      return false;
    }

    if (!fileSystemService.packageDirectoryHandle) {
      setAppState(prev => ({
        ...prev,
        error: 'No package directory selected. Select package directory first.'
      }));
      return false;
    }

    try {
      const finalName = packageName.endsWith('.zip') ? packageName : `${packageName}.zip`;

      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: true,
          operation: 'download',
          progress: 0,
          currentFile: finalName,
          error: null
        }
      }));

      const packageBlob = await packageApiService.downloadPackageBlob(finalName, (progress: number) => {
        setAppState(prev => ({
          ...prev,
          transferState: {
            ...prev.transferState,
            isTransferring: true,
            operation: 'download',
            progress,
            currentFile: finalName,
            error: null
          }
        }));
      });

      await fileSystemService.writePackageToDirectory(finalName, packageBlob);
      await handleRefreshLocalPackages();

      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          progress: 100,
          currentFile: finalName,
          error: null
        }
      }));

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          error: error instanceof Error ? error.message : `Failed to download ${packageName}`
        },
        error: error instanceof Error ? error.message : `Failed to download ${packageName}`
      }));
      return false;
    }
  };

  const handleDeleteRemotePackage = async (packageName: string) => {
    const status = packageApiService.getConnectionStatus();
    if (!status.isConfigured) {
      setAppState(prev => ({
        ...prev,
        error: 'Remote API is not configured. Configure endpoint and bearer token first.'
      }));
      return false;
    }

    try {
      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: true,
          operation: 'delete-remote',
          progress: 0,
          currentFile: packageName,
          error: null
        }
      }));

      await packageApiService.deleteRemotePackage(packageName);
      await handleRefreshRemotePackages();

      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          progress: 100,
          currentFile: packageName,
          error: null
        }
      }));

      return true;
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        transferState: {
          ...prev.transferState,
          isTransferring: false,
          operation: null,
          error: error instanceof Error ? error.message : `Failed to delete ${packageName}`
        },
        error: error instanceof Error ? error.message : `Failed to delete ${packageName}`
      }));
      return false;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  if (appState.isLoading) {
    return <LoadingScreen isLoading={true} hasProject={false} />;
  }

  // Always show the main app - the LoadingScreen is only for initial loading
  // The welcome screen with button is handled in the main app via Header component

  return (
    <HashRouter>
      <div className="themed-app flex h-screen overflow-hidden">
        {/* Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="w-64 bg-white shadow-xl z-30"
            >
              <Sidebar
                activeView={activeView}
                onViewChange={setActiveView}
                onClose={() => setSidebarOpen(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex-1 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <Header
            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
            sidebarOpen={sidebarOpen}
            onValidation={handleValidation}
            validationState={appState.validationState}
            onOpenProject={openProjectFolder}
            onOpenDriveProject={openDriveProjectFolder}
            hasProject={appState.currentFiles.length > 0}
            isFileSystemSupported={fileSystemService.isSupported}
            isDriveConfigured={appState.driveProject.isConfigured}
            projectSource={appState.projectSource}
            theme={theme}
            onThemeToggle={handleThemeToggle}
          />

          {/* Content Area */}
          <motion.div
            variants={contentVariants}
            className="flex-1 overflow-auto p-6"
          >
            <Routes>
              <Route path="/" element={<Navigate to="/explorer" replace />} />
              <Route
                path="/explorer"
                element={
                  <FileExplorer
                    files={appState.currentFiles}
                    selectedFile={appState.selectedFile}
                    onFileSelect={handleFileSelect}
                    onFileEdit={handleFileEdit}
                    onFileDownload={handleFileDownload}
                    onFileDelete={handleFileDelete}
                    onRefresh={refreshProjectFiles}
                    isRefreshing={isRefreshingFiles}
                  />
                }
              />
              <Route
                path="/validation"
                element={
                  <ValidationPanel
                    validationState={appState.validationState}
                    files={appState.currentFiles}
                    onValidation={handleValidation}
                  />
                }
              />
              <Route
                path="/version"
                element={
                  <VersionControl
                    versions={appState.versions}
                    files={appState.currentFiles}
                  />
                }
              />
              <Route
                path="/visualization"
                element={
                  <Visualization
                    data={appState.visualizationData}
                    files={appState.currentFiles}
                    theme={theme}
                  />
                }
              />
              <Route
                path="/packaging"
                element={
                  <Packaging
                    files={appState.currentFiles}
                    packages={appState.packages}
                    remotePackages={appState.remotePackages}
                    packageDirectoryState={appState.packageDirectory}
                    remotePackageState={appState.remotePackageState}
                    transferState={appState.transferState}
                    onOpenPackageDirectory={handleOpenPackageDirectory}
                    onRefreshLocalPackages={handleRefreshLocalPackages}
                    onCreatePackage={handleCreatePackage}
                    onDownloadLocalPackage={handleDownloadLocalPackage}
                    onDeleteLocalPackage={handleDeleteLocalPackage}
                    onConfigureRemote={handleConfigureRemote}
                    onRefreshRemotePackages={handleRefreshRemotePackages}
                    onUploadPackage={handleUploadPackage}
                    onDownloadRemotePackage={handleDownloadRemotePackage}
                    onDeleteRemotePackage={handleDeleteRemotePackage}
                    packageApiService={packageApiService}
                  />
                }
              />
            </Routes>
          </motion.div>
        </motion.div>

        {/* Editors */}
        <AnimatePresence>
          {showDriveBrowser && (
            <DriveBrowserModal
              isOpen={showDriveBrowser}
              onClose={() => setShowDriveBrowser(false)}
              onSelect={handleDriveBrowserSelect}
              googleDriveService={googleDriveService}
            />
          )}
          {editingFile && editorType === 'markdown' && (
            <MarkdownEditor
              file={editingFile}
              onSave={handleFileSave}
              onClose={handleEditorClose}
            />
          )}
          {editingFile && editorType === 'tsv' && (
            <TSVEditor
              file={editingFile}
              onSave={handleFileSave}
              onClose={handleEditorClose}
            />
          )}
          {editingFile && editorType === 'image' && (
            <ImageEditor
              file={editingFile}
              onSave={handleFileSave}
              onClose={handleEditorClose}
            />
          )}
        </AnimatePresence>

        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--toast-bg)',
              color: 'var(--toast-text)',
              border: '1px solid var(--toast-border)',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </HashRouter>
  );
};

export const __binaryPackagingUtils = {
  isBinaryFile,
  resolveMimeTypeForFile
};

export const __drivePackagingUtils = {
  getDriveReadMode,
  DRIVE_WARN_FILE_BYTES,
  DRIVE_HARD_FILE_BYTES,
  DRIVE_HARD_PACKAGE_BYTES,
  DRIVE_CHUNK_POLICY
};

export const __streamingPackagingUtils = {
  shouldUseStreamingZip,
  STREAMING_ZIP_MIN_TOTAL_BYTES
};

export default App;
